import type { RouteLocationNormalizedLoaded, Router } from "vue-router";
import type { OptionsDropdownOption } from "@/components/features/OptionsDropdown";
import type { Agent } from "@/schemas/agent.schema";
import type { KnowledgeCollection } from "@/schemas/knowledge.schema";
import Icon from "@/components/ui/icon/Icon";
import { resolveBreadcrumbQuery } from "@/composables/breadcrumb-query";
import { exportAgent } from "@/pages/agents/core/AgentsTable";
import { useAgentsQuery } from "@/queries/agents.query";
import { useApiKeysQuery } from "@/queries/api-keys.query";
import { useKnowledgeCollectionsQuery } from "@/queries/knowledge.query";
import { useTTSVoicesQuery } from "@/queries/tts-voices.query";
import { useWebhooksQuery } from "@/queries/webhooks.query";
import { $t } from "@/services/i18n";
import { createEllipsisText } from "@/utils/text";

export function createAgentDropdownLabel(params: any) {
  const agentsQuery = resolveBreadcrumbQuery(useAgentsQuery);

  return {
    value: params.agent_id,
    key: "agent_id",
    options: (agentsQuery.data.value ?? []).map(formatAgentOption),
  };
}

export function createAgentOptionsDropdown({
  route,
  router,
}: {
  route: RouteLocationNormalizedLoaded;
  router: Router;
}) {
  const agentsQuery = resolveBreadcrumbQuery(useAgentsQuery);
  const params = route.params;

  const agent = agentsQuery.data.value?.find((a) => a.id === params.agent_id);

  if (!agent || !params.agent_id) return [];

  const options = [] as OptionsDropdownOption[];

  // Grupo de navegação (páginas principais)
  if (route.name !== "Agent Playground") {
    options.push({
      label: $t("open_chat"),
      icon: "lucide:message-circle",
      onSelect() {
        router.push({
          name: "Agent Playground",
          params: { agent_id: agent.id, thread_id: "new" },
        });
      },
    });
  }

  if (route.name !== "Agent Conversations") {
    options.push({
      label: $t("agents:conversations"),
      icon: "lucide:message-square-text",
      onSelect() {
        router.push({
          name: "Agent Conversations",
          params: { agent_id: agent.id },
        });
      },
    });
  }

  if (route.name !== "Edit Agent") {
    options.push({
      label: $t("menu.edit_agent"),
      icon: "lucide:pencil",
      onSelect() {
        router.push({
          name: "Edit Agent",
          params: { agent_id: agent.id, tab: "profile" },
        });
      },
    });
  }

  if (route.name !== "Agent Deploy") {
    options.push({
      label: $t("deploy"),
      icon: "lucide:rocket",
      onSelect() {
        router.push(`/agents/deploy/${agent.id}`);
      },
    });
  }

  // Grupo de ações
  options.push({
    label: $t("export"),
    icon: "lucide:upload",
    onSelect: () => exportAgent(agent),
  });

  return options;
}

// Função para obter o nome formatado da voz
function getVoiceName(ttsModel: string, voiceKey: string): string {
  if (!ttsModel || !voiceKey) return voiceKey || "";

  const voicesQuery = resolveBreadcrumbQuery(useTTSVoicesQuery);
  const voices = (voicesQuery.data.value ?? {})[ttsModel] ?? [];
  const voice = voices.find((v) => v.key === voiceKey);

  return voice?.name || voiceKey;
}

export function formatAgentOption(agent: Agent) {
  return {
    btnLabel: () => (
      <div class="flex items-center gap-2 whitespace-nowrap">
        <h2 class="overflow-hidden text-ellipsis">{agent.name}</h2>
      </div>
    ),
    label: () => (
      <div class="w-full p-1">
        <h2 class="mb-1 w-full overflow-hidden text-ellipsis font-bold">{agent.name}</h2>
        <div class="flex flex-wrap gap-x-2.5 gap-y-1.5 text-xs text-muted-foreground">
          <div class="flex items-center gap-1.5">
            <Icon icon="lucide:sparkles" class="h-3 w-3 shrink-0 text-amber-9" />
            <span class="truncate">{agent.llm_model}</span>
          </div>
          {agent.tts_model && (
            <div class="flex items-center gap-1.5">
              <Icon
                icon="lucide:volume-2"
                class="h-3 w-3 shrink-0 text-cyan-9"
              />
              <span class="truncate">
                {agent.tts_voice ? getVoiceName(agent.tts_model, agent.tts_voice) : agent.tts_model}
              </span>
            </div>
          )}
        </div>
      </div>
    ),
    value: agent.id,
    textValue: agent.name,
  };
}

export function createKnowledgeDropdownLabel(params: any) {
  const knowledgeCollectionsQuery = resolveBreadcrumbQuery(useKnowledgeCollectionsQuery);

  return {
    value: params.id,
    key: "id",
    options: (knowledgeCollectionsQuery.data.value ?? []).map(formatKnowledgeOption),
  };
}

export function formatKnowledgeOption(knowledge: Omit<KnowledgeCollection, "documents">) {
  return {
    btnLabel: () => (
      <div class="flex items-center gap-2 whitespace-nowrap">
        <h2 class="overflow-hidden text-ellipsis">{knowledge.name}</h2>
      </div>
    ),
    label: () => (
      <div class="w-full p-1">
        <h2 class="w-full overflow-hidden text-ellipsis font-bold">{knowledge.name}</h2>
      </div>
    ),
    value: knowledge.id,
    textValue: knowledge.name,
  };
}

export function createWebhookDropdownLabel(params: any) {
  const webhooksQuery = resolveBreadcrumbQuery(useWebhooksQuery);

  return {
    value: params.id,
    key: "id",
    options: (webhooksQuery.data.value ?? []).map((webhook) => ({
      value: webhook.id,
      label: createEllipsisText(webhook.target_url, 40),
      textValue: createEllipsisText(webhook.target_url, 40),
    })),
  };
}

export function createApiKeyDropdownLabel(params: any) {
  const apiKeysQuery = resolveBreadcrumbQuery(useApiKeysQuery);

  return {
    value: params.id,
    key: "id",
    options: (apiKeysQuery.data.value ?? []).map((apiKey) => ({
      value: apiKey.id,
      label: apiKey.name,
      textValue: apiKey.name,
    })),
  };
}
